import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Loader2, Trash2, Wrench, ClipboardList, User, Building2, Calendar as CalIcon,
} from "lucide-react";

const STATUS_LABELS = { agendado: "Agendado", en_progreso: "En progreso", completado: "Completado", cancelado: "Cancelado" };

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}
function fromLocalInput(v) {
  if (!v) return null;
  return new Date(v).toISOString();
}

export default function JobEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [job, setJob] = useState({
    title: "",
    client: { client_id: null, name: "", rfc: "", email: "", phone: "", address: "", contact_name: "" },
    address: "",
    items: [],
    scheduled_at: toLocalInput(new Date(Date.now() + 86400000).toISOString().slice(0, 16)),
    duration_hours: 4,
    technicians: [],
    status: "agendado",
    notes: "",
  });
  const [clientMode, setClientMode] = useState("existing");

  useEffect(() => {
    (async () => {
      try {
        const [cRes, uRes] = await Promise.all([
          api.get("/clients"),
          api.get("/users").catch(() => ({ data: [] })),
        ]);
        setClients(cRes.data || []);
        setUsers((uRes.data || []).filter((u) => u.role === "tecnico" || u.role === "admin"));
        if (!isNew) {
          const r = await api.get(`/jobs/${id}`);
          setJob({ ...r.data, scheduled_at: toLocalInput(r.data.scheduled_at) });
          setClientMode(r.data.client?.client_id ? "existing" : "new");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const pickClient = (cid) => {
    if (cid === "__none__") return setJob((p) => ({ ...p, client: { client_id: null, name: "", rfc: "", email: "", phone: "", address: "", contact_name: "" } }));
    const c = clients.find((x) => x.id === cid);
    if (c) setJob((p) => ({
      ...p,
      client: { client_id: c.id, name: c.name, rfc: c.rfc || "", email: c.email || "", phone: c.phone || "", address: c.address || "", contact_name: c.contact_name || "" },
      address: p.address || c.address || "",
    }));
  };

  const toggleTech = (u) => {
    setJob((p) => {
      const has = p.technicians.some((t) => t.id === u.id);
      const next = has ? p.technicians.filter((t) => t.id !== u.id) : [...p.technicians, { id: u.id, name: u.name }];
      return { ...p, technicians: next };
    });
  };

  const save = async () => {
    if (!job.client.name?.trim()) return toast.error("Falta el nombre del cliente");
    if (!job.scheduled_at) return toast.error("Falta la fecha/hora");
    setSaving(true);
    try {
      const payload = {
        ...job,
        scheduled_at: fromLocalInput(job.scheduled_at),
        duration_hours: Number(job.duration_hours),
      };
      if (isNew) {
        const r = await api.post("/jobs", payload);
        toast.success(`Trabajo ${r.data.folio} creado`);
        nav(`/jobs/${r.data.id}`);
      } else {
        const { status, ...rest } = payload;
        const r = await api.patch(`/jobs/${id}`, { ...rest, status });
        setJob({ ...r.data, scheduled_at: toLocalInput(r.data.scheduled_at) });
        toast.success("Cambios guardados");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const generateReport = async () => {
    try {
      const r = await api.post("/reports", {
        job_id: id,
        title: `Reporte de servicio — ${job.title}`,
        client: job.client,
        site: job.address,
        service_date: fromLocalInput(job.scheduled_at),
        equipment: (job.items || []).map((it) => ({
          description: it.description || "", unit: it.unit || "PZA",
          quantity: it.quantity || 1, model: "", serial: "",
        })),
      });
      toast.success(`Reporte ${r.data.folio} creado`);
      nav(`/reports/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const del = async () => {
    if (!window.confirm("¿Eliminar este trabajo?")) return;
    try {
      await api.delete(`/jobs/${id}`);
      toast.success("Eliminado");
      nav("/calendar");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="p-8 text-slate-500">Cargando…</div>;

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6 pb-24" data-testid="job-editor">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/calendar" className="text-xs text-[#2E75B5] hover:underline flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Volver a agenda
          </Link>
          <h1 className="text-2xl lg:text-3xl font-black text-[#0A1118] tracking-tight">
            {isNew ? "Nuevo trabajo" : `${job.folio || ""} · ${job.title}`}
          </h1>
          {job.quote_folio && (
            <div className="text-sm text-slate-500 mt-1">Origen: cotización <Link to={`/quotes/${job.quote_id}`} className="text-[#005A91] font-bold hover:underline">{job.quote_folio}</Link></div>
          )}
        </div>
        {!isNew && (
          <div className="flex gap-2 flex-wrap">
            {job.report_id ? (
              <Button variant="outline" onClick={() => nav(`/reports/${job.report_id}`)} data-testid="open-report-btn" className="h-10 border-[#005A91]/20">
                <ClipboardList className="h-4 w-4 mr-1.5" /> Ver reporte
              </Button>
            ) : (job.status === "completado" || job.status === "en_progreso") && (
              <Button onClick={generateReport} data-testid="gen-report-btn" className="h-10 bg-[#4ECCC8] hover:bg-[#40b8b4] text-[#005A91] font-bold">
                <ClipboardList className="h-4 w-4 mr-1.5" /> Generar reporte
              </Button>
            )}
            <Button variant="outline" onClick={del} data-testid="delete-job-btn" className="h-10 border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <div className="pe-card p-6">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-3">Datos del trabajo</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Título</Label>
            <Input data-testid="job-title" value={job.title} onChange={(e) => setJob((p) => ({ ...p, title: e.target.value }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Fecha y hora</Label>
            <Input data-testid="job-scheduled-at" type="datetime-local" value={job.scheduled_at} onChange={(e) => setJob((p) => ({ ...p, scheduled_at: e.target.value }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Duración (horas)</Label>
            <Input data-testid="job-duration" type="number" step="0.5" value={job.duration_hours} onChange={(e) => setJob((p) => ({ ...p, duration_hours: e.target.value }))} className="mt-1 h-11 border-[#005A91]/20 font-numbers" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Dirección del servicio</Label>
            <Input data-testid="job-address" value={job.address || ""} onChange={(e) => setJob((p) => ({ ...p, address: e.target.value }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
          {!isNew && (
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Estado</Label>
              <Select value={job.status} onValueChange={(v) => setJob((p) => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="job-status-select" className="mt-1 h-11 border-[#005A91]/20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="pe-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Cliente</div>
          <div className="flex gap-1 bg-slate-100 rounded-md p-1">
            <button onClick={() => setClientMode("existing")} data-testid="job-client-existing" className={`text-xs font-bold px-3 py-1.5 rounded ${clientMode === "existing" ? "bg-white text-[#005A91] shadow-sm" : "text-slate-600"}`}>
              <User className="h-3 w-3 inline mr-1" /> Existente
            </button>
            <button onClick={() => setClientMode("new")} data-testid="job-client-new" className={`text-xs font-bold px-3 py-1.5 rounded ${clientMode === "new" ? "bg-white text-[#005A91] shadow-sm" : "text-slate-600"}`}>
              <Building2 className="h-3 w-3 inline mr-1" /> Nuevo
            </button>
          </div>
        </div>
        {clientMode === "existing" && (
          <Select value={job.client.client_id || "__none__"} onValueChange={pickClient}>
            <SelectTrigger data-testid="job-client-select" className="h-11 border-[#005A91]/20"><SelectValue placeholder="Elegir cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Ninguno</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.rfc ? ` — ${c.rfc}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre / Razón social</Label>
            <Input data-testid="job-client-name" value={job.client.name} onChange={(e) => setJob((p) => ({ ...p, client: { ...p.client, client_id: null, name: e.target.value } }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Contacto</Label>
            <Input data-testid="job-client-contact" value={job.client.contact_name || ""} onChange={(e) => setJob((p) => ({ ...p, client: { ...p.client, contact_name: e.target.value } }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
        </div>
      </div>

      {users.length > 0 && (
        <div className="pe-card p-6">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-3">Técnicos asignados</div>
          <div className="flex gap-2 flex-wrap">
            {users.map((u) => {
              const active = job.technicians.some((t) => t.id === u.id);
              return (
                <button
                  key={u.id}
                  onClick={() => toggleTech(u)}
                  data-testid={`tech-toggle-${u.id}`}
                  className={`text-sm font-bold px-4 py-2 rounded-full border-2 transition-colors duration-150 ${
                    active ? "bg-[#4ECCC8] border-[#4ECCC8] text-[#005A91]" : "bg-white border-slate-300 text-slate-600 hover:border-[#005A91]/40"
                  }`}
                >
                  <Wrench className="h-3.5 w-3.5 inline mr-1.5" /> {u.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="pe-card p-6">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Notas</div>
        <Textarea data-testid="job-notes" rows={4} value={job.notes || ""} onChange={(e) => setJob((p) => ({ ...p, notes: e.target.value }))} className="border-[#005A91]/20 focus-visible:ring-[#4ECCC8]" />
      </div>

      <div className="flex justify-end sticky bottom-4">
        <Button onClick={save} disabled={saving} data-testid="job-save-btn" className="h-12 bg-[#2E75B5] hover:bg-[#3a88cd] font-bold px-6 shadow-lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {isNew ? "Crear trabajo" : "Guardar cambios"}
        </Button>
      </div>
    </div>
  );
}
